import styled, { css } from 'styled-components';
import { Typography } from '../../global/theme';
import { mobile_xl } from '../../global/mediaQueryMixin';
import { AlignCenter, HideScrollbar, LineClamp } from '../../global/commonStyle';

interface Props {
  isWidth?: boolean;
  isFontWeight?: boolean;
  isBorder?: boolean;
  isBorderBottom?: boolean;
  isBorderTop?: boolean;
  isScrollTable?: boolean;
  isLastChildSticky?: boolean;
}

const SmallHeader = styled.th<Props>`
  background: var(--background-tertiary);
  color: var(--text-secondary);
  ${Typography.body_sm_medium}
  text-transform: uppercase;
  padding: var(--space-10) var(--space-12);
  text-align: start;
  white-space: nowrap;
  border-top: 0.5px solid var(--border-primary);

  ${(props) =>
    props.className === 'no-border-right' &&
    css`
      border-right: none;
    `}

  ${(props: Props) =>
    props.isBorderBottom &&
    css`
      border-bottom: 0.5px solid var(--border-primary);
    `}

    ${({ isLastChildSticky, isScrollTable }) =>
    isScrollTable &&
    css`
      &:first-child {
        position: sticky;
        left: 0;
        box-shadow: var(--shadow-card);
        z-index: var(--z-index-base);
        ${mobile_xl(css`
          position: relative;
          box-shadow: unset;
        `)}
      }

      ${isLastChildSticky &&
      css`
        &:last-child {
          position: sticky;
          right: 0;
          z-index: var(--z-index-base);
          box-shadow: var(--shadow-card);
        }
      `}
    `}
`;
const Table = styled.table<Props>`
  border-collapse: separate;
  border-style: hidden;
  border-radius: var(--border-radius-0) var(--border-radius-0) var(--border-radius-8) var(--border-radius-8);
  border: 0.5px solid var(--border-primary);
  border-top: none;
  border-spacing: 0;
  background: var(--background-primary);
  width: 100%;
  .reports-time-empty {
    margin: var(--space-0) auto var(--space-24);
    ${mobile_xl(css`
      margin-bottom: var(--space-16);
    `)}
  }
  ${mobile_xl(css`
    border: unset;
  `)}
  ${(props: Props) =>
    props.isScrollTable &&
    css`
      border: none;
    `}
`;
const TableData = styled.td<Props>`
  border-top: 0.5px solid var(--border-primary);
  text-align: start;
  padding: var(--space-12);
  color: var(--text-primary);
  ${Typography.body_md_medium}
  ${(props: Props) =>
    props.isFontWeight &&
    css`
      font-weight: 700;
    `}
  ${(props: Props) =>
    props.isBorderTop &&
    css`
      border-top: none;
    `}
  ${(props: Props) =>
    props.isWidth &&
    css`
      width: 20%;
    `}
      ${mobile_xl(css`
    white-space: nowrap;
    min-width: 50px;
  `)}
  &:first-child {
    ${(props: Props) =>
      props.isWidth &&
      css`
        width: 60%;
      `}
  }
  &:first-child {
    ${(props: Props) =>
      props.isBorder &&
      css`
        width: 43px;
        text-align: center;
        ${mobile_xl(css`
          min-width: 30px;
        `)}
      `}
  }
  ${(props: Props) =>
    props.isBorder &&
    css`
      padding: var(--space-10) var(--space-12);
    `}

  .truncate-text {
    cursor: pointer;
    display: inline-flex;
    max-width: 249px;
    p {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin: var(--space-0);
    }
  }
  .tooltip-text {
    max-width: 249px;
    width: max-content;
  }
  .screen-shot {
    cursor: pointer;
    border: 0.5px solid var(--border-primary);
  }
  .image-data {
    ${AlignCenter}
    gap: var(--space-18);
    a {
      width: 36px;
      height: 36px;
    }
  }
  ${({ isScrollTable, isLastChildSticky }) =>
    isScrollTable &&
    css`
      &:first-child {
        position: sticky;
        left: 0;
        z-index: var(--z-index-base);
        box-shadow: var(--shadow-card);
        background: var(--background-primary);
        ${mobile_xl(css`
          position: relative;
          box-shadow: unset;
        `)}
      }

      ${isLastChildSticky &&
      css`
        &:last-child {
          position: sticky;
          right: 0;
          z-index: var(--z-index-base);
          background: var(--background-primary);
          box-shadow: var(--shadow-card);
        }
      `}
    `}
`;
const ProductName = styled.div<Props>`
  color: var(--text-primary);
  ${Typography.body_md_medium}
  margin: var(--space-0);
  ${LineClamp(2)}
  word-break: break-all;
  white-space: normal;

  ${(props: Props) =>
    props.isFontWeight &&
    css`
      font-weight: 700;
    `}
`;

const Link = styled.div`
  ${AlignCenter}
  gap: var(--space-16);
  justify-content: flex-end;
  cursor: pointer;
`;

const Row = styled.div`
  display: flex;
  gap: var(--space-10);
`;

const Icon = styled.div`
  height: 16px;
  width: 16px;
  .plus-minus-icon {
    path {
      stroke: var(--brand-primary);
    }
  }
`;
const TableWrapper = styled.div<Props>`
  ${(props: Props) =>
    props.isScrollTable &&
    css`
      -webkit-overflow-scrolling: touch;
      position: relative;
      border-radius: var(--border-radius-0) var(--border-radius-0) var(--border-radius-8) var(--border-radius-8);
      border: 0.5px solid var(--border-primary);
      border-style: none solid solid solid;
      ${HideScrollbar}
    `}
  ${mobile_xl(css`
    border-radius: var(--border-radius-0) var(--border-radius-0) var(--border-radius-8) var(--border-radius-8);
    border: 0.5px solid var(--border-primary);
    border-top: none;
    width: calc(100vw - 42px);
    ${HideScrollbar}
  `)}
`;

const Date = styled.div`
  .month {
    color: var(--brand-primary);
    display: block;
    ${Typography.body_sm_medium}
  }

  .day {
    color: var(--text-primary);
    display: block;
    margin-top: var(--space-3);
    ${Typography.body_md_medium}
  }
  ${mobile_xl(css`
    width: max-content;
  `)}
`;

export { ProductName, TableData, SmallHeader, Table, Link, Icon, Row, TableWrapper, Date };
